import {
  Alert,
  Box,
  Button,
  Pagination,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo, useRef, useState } from "react";

import { productQuery } from "../../hooks/queries/useProduct";
import { useProductSearch } from "../../hooks/queries/useProductSearch";
import { KeywordMode, ProductCard } from "../../types/productPlane";
import CompareTrayBar from "../Compare/CompareTrayBar";
import ExportFieldsButton from "../common/ExportFieldsButton";
import LogoLoader from "../common/LogoLoader";
import ActiveFilterChips from "../Filters/ActiveFilterChips";
import ResizeHandle from "../Filters/ResizeHandle";
import { useResizableWidth } from "../Filters/useResizableWidth";
import ExploreFilterPanel from "./ExploreFilterPanel";
import ExploreFilterPanelPlaceholder from "./ExploreFilterPanelPlaceholder";
import ExploreResultsHead from "./ExploreResultsHead";
import ExploreSuggestField from "./ExploreSuggestField";
import ExploreTabs from "./ExploreTabs";
import ProductResultCard from "./ProductResultCard";
import ProductResultTable from "./ProductResultTable";
import {
  ALL_FILTER_VALUES,
  ExploreNarrow,
  PRODUCT_NOUN as NOUN,
  PRODUCT_SEARCH_PLACEHOLDER as SEARCH_PLACEHOLDER,
  exploreActiveFilters,
  noFilterNames,
  rememberFilterNames,
} from "./exploreFilters";
import {
  EXPLORE_PAGE_SIZE,
  NO_EXPLORE_FILTERS,
  countExploreFilters,
  toProductSearchParams,
  toProductSelectionParams,
  withExplorePick,
} from "./exploreState";
import { PRODUCT_EXPORT_FIELDS, exportProducts } from "./productExport";
import { EXPLORE_TABS, PRODUCT_TAB } from "./exploreTabs";
import { useExploreState } from "./useExploreState";

// Width (px) of the column the filter panel takes, left of the results
export const EXPLORE_FILTER_COLUMN_WIDTH = 250;

// The least and the most (px) the user may drag that column to
export const EXPLORE_FILTER_COLUMN_MIN_WIDTH = 200;
export const EXPLORE_FILTER_COLUMN_MAX_WIDTH = 480;

// Where the browser keeps the width the user chose for that column
export const EXPLORE_FILTER_WIDTH_STORAGE_KEY = "mi.explore.filterWidth";

// The quiet caption ahead of the chips, by how the keyword was matched
const KEYWORD_MODE_CAPTIONS: Record<KeywordMode, string> = {
  all: "All words matched",
  any: "Any word matched",
};

export interface ExploreViewProps {
  // Opens a product of the list, or one a suggestion names. Explore passes
  // none while there is no product view; then a card and a table row are
  // plain text.
  onOpenProduct?: (product: Pick<ProductCard, "id" | "type">) => void;
  // The id of the product that is open beside the list; it is marked
  openProductId?: string;
}

// Explore: a keyword search over products, narrowed by filters, shown as
// cards or as a table, in pages. What it shows is held in the address (see
// exploreState).
const ExploreView: React.FC<ExploreViewProps> = ({
  onOpenProduct,
  openProductId,
}) => {
  const [state, change] = useExploreState();

  // The width of the filter column, as the user left it in this browser
  const filterWidth = useResizableWidth({
    defaultWidth: EXPLORE_FILTER_COLUMN_WIDTH,
    min: EXPLORE_FILTER_COLUMN_MIN_WIDTH,
    max: EXPLORE_FILTER_COLUMN_MAX_WIDTH,
    storageKey: EXPLORE_FILTER_WIDTH_STORAGE_KEY,
  });

  // What the filter panel asks for beside the state. Neither is part of the
  // address: the text typed into the box of one long filter, and whether a
  // cut list was asked for in whole.
  const [narrow, setNarrow] = useState<ExploreNarrow | null>(null);
  const [allFilterValues, setAllFilterValues] = useState(false);

  // One request per state: the page of results and the whole filter panel
  // The products the state selects, which the suggestions are counted under
  const selection = useMemo(() => toProductSelectionParams(state), [state]);
  const request = useMemo(
    () =>
      toProductSearchParams(state, {
        filterLimit: allFilterValues ? ALL_FILTER_VALUES : undefined,
        narrow,
      }),
    [state, narrow, allFilterValues],
  );
  const { data, error, isPending, isPlaceholderData } =
    useProductSearch(request);

  const products: ProductCard[] = data?.results.items ?? [];
  const total = data?.results.total ?? 0;
  const pageCount = Math.ceil(total / EXPLORE_PAGE_SIZE);

  const filters = data?.filters ?? null;
  // The names the answers so far gave the filter values, for the chips
  const filterNames = useRef(noFilterNames());
  const activeFilters = useMemo(() => {
    if (filters) {
      rememberFilterNames(filterNames.current, filters);
    }
    return exploreActiveFilters(state, filterNames.current);
  }, [state, filters]);
  // The file of the page of results on screen, each product read in full
  // through the cache the product page reads from
  const queryClient = useQueryClient();
  const exportControl = (
    <ExportFieldsButton
      fields={PRODUCT_EXPORT_FIELDS}
      storageKey="mi.export.explore.products"
      title="Export products"
      note={`The ${products.length} products on this page, each with the details of its page`}
      onExport={(format, keys, progress) =>
        exportProducts({
          state,
          active: activeFilters,
          ids: products.map((product) => product.id),
          offset: (state.page - 1) * EXPLORE_PAGE_SIZE,
          total,
          format,
          keys,
          read: (id) => queryClient.fetchQuery(productQuery(id)),
          progress,
        })
      }
    />
  );
  // How the answer matched the keyword. Null without a keyword, also while
  // the answer to a keyword just cleared is still the one shown.
  const keywordMode: KeywordMode | null = state.keyword
    ? (data?.keyword_mode ?? null)
    : null;
  // Whether the first answer is still on its way
  const loading = !error && (isPending || !data);

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        px: 3,
        py: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        backgroundColor: "background.default",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Explore
        </Typography>
        <ExploreTabs
          tabs={EXPLORE_TABS}
          current={PRODUCT_TAB.type}
          count={data?.scope_total}
        />
      </Box>

      <ExploreSuggestField
        selection={selection}
        keyword={state.keyword}
        onSubmit={(keyword) => change({ keyword })}
        subject={NOUN}
        placeholder={SEARCH_PLACEHOLDER}
        onPickSuggestion={({ group, suggestion }) => {
          if (group === "product") {
            onOpenProduct?.({ id: suggestion.value, type: "product" });
            return;
          }
          // The chip names the pick at once, before the answer does
          const names = filterNames.current.values[group] ?? new Map();
          filterNames.current.values[group] = names.set(
            suggestion.value,
            suggestion.name,
          );
          change({
            [group]: withExplorePick(state[group], suggestion.value, true),
          });
        }}
      />

      {/* The row under the field: how the keyword was matched, and the
          chips of the active filters */}
      {(keywordMode || activeFilters.length > 0) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          {keywordMode && (
            <Typography variant="caption" color="text.disabled">
              {KEYWORD_MODE_CAPTIONS[keywordMode]}
            </Typography>
          )}
          <ActiveFilterChips
            filters={activeFilters}
            onRemove={(id) => {
              const removed = activeFilters.find((filter) => filter.id === id);
              if (removed) {
                change(removed.remove);
              }
            }}
            onClearAll={() => change(NO_EXPLORE_FILTERS)}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${filterWidth.width}px minmax(0, 1fr)`,
          columnGap: 3,
          alignItems: "start",
          // Down to the bottom of the page, so that the placeholder panel
          // reaches it while the results are short or loading
          flex: "1 0 auto",
        }}
      >
        {/* The filter panel's column. Until the first answer brings the
            filters, it holds the panel's frame with placeholder sections.
            The handle on its right border resizes it. */}
        <Box
          sx={{
            position: "relative",
            alignSelf: "stretch",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {filters ? (
            <ExploreFilterPanel
              filters={filters}
              state={state}
              onChange={change}
              narrow={narrow}
              onNarrowChange={setNarrow}
              onShowAll={() => setAllFilterValues(true)}
            />
          ) : (
            !error && (
              <ExploreFilterPanelPlaceholder
                activeCount={countExploreFilters(state)}
              />
            )
          )}
          <ResizeHandle resizable={filterWidth} />
        </Box>

        <Box
          component="section"
          aria-label="Results"
          sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
        >
          {error ? (
            <Alert severity="error">Products could not be loaded.</Alert>
          ) : loading || !data ? (
            // In the middle of the screen, not of the results' column, which
            // the navigation and the filter panel push to the right; up a
            // little from the true middle, where the eye rests
            <Box
              sx={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, calc(-50% - 32px))",
              }}
            >
              <LogoLoader size="view" label="Loading products" />
            </Box>
          ) : (
            <>
              <ExploreResultsHead
                total={total}
                scopeTotal={data.scope_total}
                noun={NOUN}
                sort={state.sort}
                onSortChange={(sort) => change({ sort })}
                view={state.view}
                onViewChange={(view) => change({ view })}
                exportControl={exportControl}
              />

              {products.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    py: 6,
                  }}
                >
                  <Typography color="text.secondary">
                    No products found.
                  </Typography>
                  {state.keyword && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => change({ keyword: "" })}
                    >
                      Clear keyword
                    </Button>
                  )}
                  {activeFilters.length > 0 && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => change(NO_EXPLORE_FILTERS)}
                    >
                      Clear filters
                    </Button>
                  )}
                </Box>
              ) : (
                // The previous page stays, dimmed, while the next one loads
                <Box
                  sx={{
                    opacity: isPlaceholderData ? 0.6 : 1,
                    transition: "opacity 120ms",
                  }}
                >
                  {state.view === "table" ? (
                    <ProductResultTable
                      products={products}
                      sort={state.sort}
                      onSortChange={(sort) => change({ sort })}
                      onOpen={onOpenProduct}
                      openProductId={openProductId}
                      loading={isPlaceholderData}
                    />
                  ) : (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {products.map((product) => (
                        <ProductResultCard
                          key={product.id}
                          product={product}
                          onOpen={onOpenProduct}
                          open={product.id === openProductId}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}

              {pageCount > 1 && (
                <Pagination
                  aria-label="Result pages"
                  count={pageCount}
                  page={Math.min(state.page, pageCount)}
                  onChange={(_event, page) => change({ page })}
                  size="small"
                  sx={{ alignSelf: "center" }}
                />
              )}
            </>
          )}
        </Box>
      </Box>

      {/* The products picked for comparison, in view under the results */}
      <CompareTrayBar />
    </Box>
  );
};

export default ExploreView;
